import { hire, makeOffer, reject, type ConsiderationAfterAction } from '@/api/considerations';
import type { PostPath } from '@/api/http';
import type { ConsiderationStatus } from '@/api/vocabularies';

/**
 * **The three status actions, and which of them each status offers** — `DESIGN.md` §6.5, ADR 0010.
 *
 * ⚠️ **"Button" below means "menu item" since 2026-08-11, and the prose below was not reworded for it —
 * this note is the module's only edit, and no value in it changed.** TASK-125 moved the three actions into
 * the run's own overflow menu (`DESIGN.md` §6.5, *The block's composition*); the offered sets, their
 * reasons and `variant` are unchanged, so the sentences
 * below still describe **what is offered and why**, and only the shape of the control moved. `variant`'s
 * one reader is now `destructive: STATUS_ACTIONS[action].variant === 'danger'` in `StatusActions.tsx`,
 * which is §4's rule read as **meaning** — `negative` text in a menu, a `negative` outline as a button,
 * a fill in neither. This note exists because the sprint's own `code-reviewer` found the file describing
 * a rendering that no longer exists, which is the failure class this project has paid for repeatedly.
 *
 * ⚠️ **This is an affordance, not a state machine, and the distinction is the whole task.** ADR 0010
 * has **no transition rules**: its table is total at fifteen cells, every action is available from
 * every state (`REJECTED` included — *"an offer after a rejection is a real thing that happens"*),
 * and the server refuses nothing on funnel grounds. So nothing here enforces anything, and a shorter
 * list below is never a claim that the longer one would be rejected.
 *
 * What ADR 0010 *does* make unrepresentable is a status set **by value**: no dropdown, no radio group,
 * no `status` field in any request body — the three calls below take **no body at all**. That is the
 * invariant this module defends (`backend/tests/test_status_is_not_writable.py` is its server-side
 * twin), and it is why a five-value `Select` is not a simplification but a deletion of it.
 *
 * §6.5 fixes one row of the table — `INTERVIEWING`: *Make offer*, *Reject* — and the other four were
 * decided with TASK-066 and written into §6.5 in the same pass. Three rules produced them, in order:
 *
 *  1. **An action whose outcome is already the status is not offered.** The API answers 200, changes
 *     nothing and writes **no** log row (`perform_status_action` is idempotent), so the button would
 *     do nothing visible — which reads as a bug and teaches people not to trust the others.
 *  2. **A hire is offered where an offer is outstanding, and nowhere else.** §6.5's `INTERVIEWING`
 *     set says so by leaving *Hired* out of it, and an offer precedes a hire in every process this
 *     product has met. It is still one click away from every state through *Make offer*.
 *  3. **Every state a button can leave you in offers a way onwards.** A mis-click on a card is
 *     recoverable without an administrator: `HIRED` offers *Make offer* (back to an outstanding
 *     offer) and *Reject*, and `REJECTED` offers *Make offer* — which is also the one sequence that
 *     can answer 409 (a rejected run, a newer active run at the same vacancy, and *Make offer* on the
 *     old one).
 *
 * ⚠️ **No button ever sets `NEW` or `INTERVIEWING`**, because no action does. `INTERVIEWING` is a
 * consequence of the **scheduling act** — `POST /considerations/{id}/interviews/`, ADR 0010's own
 * *"creating the first interview — set automatically"* — and a `POST …/start-interviewing/` would be
 * the dropdown this ADR refuses with extra steps. So a move out of `NEW` or `INTERVIEWING` cannot be
 * undone from here, and that is a property of the ADR rather than of this screen.
 *
 * ⚠️ **Nothing here reads feedback.** ADR 0010 rejected automatic outcome derivation outright
 * (*"all recommendations `YES` → propose an offer"* is hiring policy encoded in software, and under
 * white-labelling every client's policy differs), so the set is a function of the **status alone** —
 * never of `average_score`, `latest_recommendation` or any interview row. A signature that could not
 * see them is the cheapest guarantee of that.
 */

/** One action's URL segment, read off a path literal of the generated schema. */
type ActionSegmentOf<Path> =
  Path extends `/api/v1/considerations/{consideration_id}/${infer Segment}/` ? Segment : never;

/**
 * The three actions, **derived from the paths the OpenAPI document declares** rather than typed out
 * (`.claude/rules/frontend.md`: types come from the schema; `api.test.ts` fails a hand-written
 * vocabulary, and it failed this one first). The document carries no `ConsiderationAction` schema —
 * the action is the URL, not a field — so the URLs are what there is to derive from, and that is the
 * right thing to derive from here: an action **is** an endpoint (ADR 0010).
 *
 * ⚠️ `interviews` is excluded, and it is the ADR's own point rather than a tidy-up: scheduling is
 * `POST /considerations/{id}/interviews/`, it sets `INTERVIEWING` as a **consequence**, and *"it is
 * not a fourth action — there is no `POST …/start-interviewing/`: that would be the status dropdown
 * this ADR refuses, with extra steps"*.
 *
 * A fourth status endpoint added on the backend therefore stops `tsc` at `STATUS_ACTIONS` below,
 * which is the one place that would have to decide what it is called and where it is offered.
 */
export type StatusAction = Exclude<ActionSegmentOf<PostPath>, 'interviews'>;

interface StatusActionSpec {
  /**
   * The status this action results in — ADR 0010's table, and the **only** thing the status is ever
   * compared against here (rule 1 above). It is not a transition rule: it is what the outcome is.
   */
  readonly outcome: ConsiderationStatus;
  /** Its label, in the `candidates` namespace (§11: no English literal in this module). */
  readonly labelKey: string;
  /**
   * §4: `danger` is **outlined in `negative`, never filled** — the warm accent sits near `negative`
   * in hue, and a filled warm button is always the primary action and never a rejection.
   */
  readonly variant: 'secondary' | 'danger';
  /** The call, which takes **no body**: `apiPost(path, undefined, …)` in `considerations.ts`. */
  readonly call: (considerationId: number) => Promise<ConsiderationAfterAction>;
}

/**
 * The three actions, keyed by the **URL segment** the API names them with (`…/make-offer/`), which is
 * the same spelling `ConsiderationAction` uses on the backend.
 */
export const STATUS_ACTIONS: Record<StatusAction, StatusActionSpec> = {
  'make-offer': {
    outcome: 'OFFER',
    labelKey: 'card.actions.makeOffer',
    variant: 'secondary',
    call: makeOffer,
  },
  hire: {
    outcome: 'HIRED',
    labelKey: 'card.actions.hire',
    variant: 'secondary',
    call: hire,
  },
  reject: {
    outcome: 'REJECTED',
    labelKey: 'card.actions.reject',
    variant: 'danger',
    call: reject,
  },
};

/**
 * The offered set per status, in the order the buttons are rendered.
 *
 * The `Record<ConsiderationStatus, …>` is load-bearing: a status added to the backend's enum stops
 * `tsc` **here**, at the one place that has to decide what to offer for it, rather than rendering a
 * block with no actions and no complaint (`vocabularies.ts` keeps its own two lists derived the same
 * way).
 */
export const STATUS_AFFORDANCE: Record<ConsiderationStatus, readonly StatusAction[]> = {
  /** Nobody has spoken to them yet: the offer straight out of a screening, or a no. */
  NEW: ['make-offer', 'reject'],
  /** §6.5, verbatim: *"from `INTERVIEWING`: Make offer, Reject"*. */
  INTERVIEWING: ['make-offer', 'reject'],
  /** The two ways an outstanding offer ends. *Make offer* is the status already (rule 1). */
  OFFER: ['hire', 'reject'],
  /** The corrections: back to an outstanding offer, or the hire fell through. */
  HIRED: ['make-offer', 'reject'],
  /** A second round after a rejection is ordinary (ADR 0010). *Reject* is the status already. */
  REJECTED: ['make-offer'],
} as const;

/** What §6.5's block offers for a status — the only reader of the table above. */
export const actionsFor = (status: ConsiderationStatus): readonly StatusAction[] =>
  STATUS_AFFORDANCE[status];
